import { getCurrentOrg } from '../../../shared/organization/organizationActions';
import { getDatabaseService } from '../../../shared/services/firebaseConfig';
import { DataSnapshot } from 'firebase/database';
import { diagramsRef, loadDiagramFromFirestore } from './diagram.firestore';
import { Operation } from '../model/operation.model';
import { DiagramModel } from '@diagram/model/diagram.model';

export const rtdbDiagramRef = async (diagramId: string, path: string) => {
  try {
    const org = getCurrentOrg();
    if (!org || !org.id) {
      throw new Error('Organization not set. Cannot access Realtime Database.');
    }
    const rtdb = await getDatabaseService();
    const { ref } = await import('firebase/database');
    return ref(rtdb, `realtime/${diagramId}${path ? `/${path}` : ''}`);
  } catch (error) {
    console.error('Error accessing RTDB:', error);
    throw new Error('Organization not set. Cannot access Realtime Database.');
  }
};

// export const loadDiagramFromRTDB = async (diagramId: string) => {
//   const rtdbRef = await rtdbDiagramRef(diagramId, '');
//   const { get, set } = await import('firebase/database');
//   const snapshot = await get(rtdbRef);
//   if (snapshot.exists()) {
//     return snapshot.val();
//   } else {
//     console.log('Populating RTDB from Firestore');
//     const firestoreData = await loadDiagramFromFirestore(diagramId);
//     await set(rtdbRef, {
//       ...firestoreData.diagramData,
//       nodes: firestoreData.nodes,
//       edges: firestoreData.edges,
//       drillDowns: firestoreData.drillDowns,
//       swimlanes: firestoreData.swimlanes,
//     });
//     return firestoreData;
//   }
// };

type RtdbHandlers = {
  receiveOperation: (operation: Operation) => void;
  receiveIndex: (index: number) => void;
  receiveUpdate: (data: Partial<DiagramModel>) => void;
};

export const subscribeToDiagramV2 = async (
  diagramId: string,
  handlers: RtdbHandlers,
) => {
  const { onSnapshot } = await import('firebase/firestore');
  const diagramRef = await diagramsRef(diagramId);
  const dataRef = await rtdbDiagramRef(diagramId, '');
  const operationsRef = await rtdbDiagramRef(diagramId, '/session/operations');
  const indexRef = await rtdbDiagramRef(diagramId, '/session/nodeIndex');
  const { onChildAdded, onValue, off } = await import('firebase/database');

  const handleOperationAdded = (snapshot: DataSnapshot) => {
    const operation = snapshot.val();
    handlers.receiveOperation(operation);
  };

  const handleIndexChange = (snapshot: DataSnapshot) => {
    const newIndex = snapshot.val();
    handlers.receiveIndex(newIndex);
  };

  const handleDataChange = (snapshot: DataSnapshot) => {
    const data = snapshot.val();
    handlers.receiveUpdate(data);
  };

  const unsubscribeDoc = onSnapshot(diagramRef, (docSnapshot) => {
    if (docSnapshot.exists()) {
      const id = docSnapshot.id;
      const diagramData = docSnapshot.data();
      console.log('Diagram data updated:', { id, ...diagramData });
    }
  });
  onChildAdded(operationsRef, handleOperationAdded);
  onValue(indexRef, handleIndexChange);
  onValue(dataRef, handleDataChange);

  return () => {
    off(operationsRef, 'child_added', handleOperationAdded);
    off(indexRef, 'value', handleIndexChange);
    off(dataRef, 'value', handleDataChange);
    unsubscribeDoc();
  };
};

export const sendOpToRTDB = async (diagramId: string, operation: Operation) => {
  const operationsRef = await rtdbDiagramRef(diagramId, '/session/operations');
  const { push } = await import('firebase/database');
  await push(operationsRef, operation);
};

export const incrementNodeIndexInRTDB = async (
  diagramId: string,
  index: number = 100,
) => {
  const ref = await rtdbDiagramRef(diagramId, '/session');
  const { increment, update } = await import('firebase/database');
  await update(ref, { nodeIndex: increment(index) });
};

export const updateDiagramDataInRTDB = async (
  diagramId: string,
  data: Partial<DiagramModel>,
) => {
  const ref = await rtdbDiagramRef(diagramId, '');
  const { update } = await import('firebase/database');
  await update(ref, data);
};

export const bulkSendOpsToRTDB = async (
  diagramId: string,
  operations: Operation[],
) => {
  const { set } = await import('firebase/database');

  for (const operation of operations) {
    const operationsRef = await rtdbDiagramRef(
      diagramId,
      `/session/operations/${operation.opId}`,
    );
    await set(operationsRef, operation);
  }
};

export const checkForSessionInRTDB = async (diagramId: string) => {
  const rtdbRef = await rtdbDiagramRef(diagramId, '/session');
  const { get, set } = await import('firebase/database');
  const snapshot = await get(rtdbRef);
  if (!snapshot.exists()) {
    await set(rtdbRef, { operations: {}, createdAt: Date.now() });
    return true;
  } else {
    return true;
  }
};

export const loadDiagramFromRTDB = async (diagramId: string) => {
  const rtdbRef = await rtdbDiagramRef(diagramId, '');
  const { get } = await import('firebase/database');
  try {
    const snapshot = await get(rtdbRef);
    const data = await loadDiagramFromFirestore(diagramId);
    console.log('data loaded from firestore', data);
    if (snapshot.exists() && snapshot.val()?.name) {
      return {
        diagram: { ...snapshot.val(), id: diagramId } as DiagramModel,
        rfNodes: Object.values(data.nodes).sort(
          (a, b) => a.data.index - b.data.index,
        ),
        rfEdges: Object.values(data.edges),
        nodes: data.nodes,
        edges: data.edges,
        drilldowns: data.drilldowns,
      };
    } else {
      const { set } = await import('firebase/database');
      await set(rtdbRef, {
        ...data.diagram,
      });
      return {
        diagram: data.diagram,
        rfNodes: Object.values(data.nodes).sort(
          (a, b) => a.data.index - b.data.index,
        ),
        rfEdges: Object.values(data.edges),
        nodes: data.nodes,
        edges: data.edges,
        drilldowns: data.drilldowns,
      };
    }
  } catch (error) {
    console.error('Error loading diagram from RTDB:', error);
    throw error;
  }
};
